import AIPages from "ai";
import { AppContext } from "App";
import Header from "components/base/Header";
import CurrencyProvider from "CurrencyProvider";
import localforage from "localforage";
import ModalProvider from "ModalProvider";
import OdooProvider from "OdooProvider";
import AccountingPages from "pages/accounting";
import Assessment from "pages/assessment";
import ComplianceRoutes from "pages/compliance";
import DevelopmentRoutes from "pages/development";
import IncPages from "pages/inc";
import ObjectivePage from "pages/objective";
import OdooPages from "pages/odoo";
import OrderScheduler from "pages/orderScheduler";
import PageNotFound from "pages/PageNotFound";
import SupplierCalendar from "pages/production/supplierCalendar";
import ProductsNew from "pages/products";
import GearsPages from "pages/products/gears";
import ProductReleasePages from "pages/products/release";
import RetailerStorePages from "pages/retailer";
import SalesPages from "pages/sales";
import SentryTestPage from "pages/SentryTestPage";
import SubSupplierPage from "pages/subSupplier";
import SuppliersPage from "pages/suppliers";
import TestReportsPage from "pages/testReports";
import BarcodePage from "pages/tools/barcode";
import UserProfilePage from "pages/userProfile";
import Validator from "pages/validate";
import { useContext, useEffect, useState } from "react";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { INTERNAL, QC } from "utils/permissions";
import { trackPageView, trackUser } from "utils/track";
import Spinner from "./components/base/Spinner";
import { useFetchMe } from "./hooks/user";
import Amazon from "./pages/amazon";
import Analytics from "./pages/analytics";
import CalculatorV2 from "./pages/calculator";
import ITRoutes from "./pages/itWork/ITRoutes";
import ManageIndex from "./pages/manage";
import Materials from "./pages/materials";
import PricePages from "./pages/price";
import Production from "./pages/production";
import ProductList from "./pages/productList";
import Products from "./pages/products/v3";
import QCPage from "./pages/qc";
import Shipment from "./pages/shipment";
import Stock from "./pages/stock";
import ToolsPage from "./pages/tools";

localforage.config({
  driver: localforage.INDEXEDDB,
  name: "wis",
  storeName: "wisStore",
  version: 1.0,
});

const publicPaths = ["/barcode"];

const AppRoutes = () => {
  const { loading, data, error } = useFetchMe();
  const [inited, setInited] = useState(false);
  const { setUser, hasPermission } = useContext(AppContext);
  const location = useLocation();

  useEffect(() => {
    trackPageView();
  }, [location]);

  useEffect(() => {
    if (!loading) {
      if (data) {
        setUser(data.me);
        trackUser(data.me);
      }
      if (error) {
        if (publicPaths.every((path) => !location.pathname.startsWith(path))) {
          window.location.href =
            process.env.REACT_APP_AUTH_SERVER +
            "?redirectTo=" +
            encodeURIComponent(window.location.href);
        }
      }
      setInited(true);
    }
  }, [loading, data]);

  if (!inited) return <Spinner />;

  if (hasPermission(INTERNAL)) return <InternalRoutes />;
  if (hasPermission(QC)) return <QCRoutes />;

  return (
    <Routes>
      <Route path="/barcode" element={<BarcodePage />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
};

const InternalRoutes = () => {
  const { showMainMenu } = useContext(AppContext);
  return (
    <Routes>
      <Route
        element={
          <CurrencyProvider>
            <OdooProvider>
              <ModalProvider>
                <div>
                  {showMainMenu ? <Header /> : null}
                  <div
                    className={`
                      relative z-20 text-xs flex flex-1 flex-col min-h-screen transform-gpu duration-300 transition-all
                        md:block ${showMainMenu ? "md:ml-52" : ""}
                    `}
                    data-testid="main-content"
                  >
                    <Outlet />
                  </div>
                </div>
              </ModalProvider>
            </OdooProvider>
          </CurrencyProvider>
        }
      >
        <Route path="development/*" element={<DevelopmentRoutes />} />
        <Route path="ai/*" element={<AIPages />} />
        <Route path="objectives/*" element={<ObjectivePage />} />
        <Route path="products-new/*" element={<ProductsNew />} />
        <Route path="products/*" element={<Products />} />
        <Route path="release/*" element={<ProductReleasePages />} />
        <Route path="gears/*" element={<GearsPages />} />
        <Route path="product-list/*" element={<ProductList />} />
        <Route path="certificates/*" element={<ComplianceRoutes />} />
        <Route path="calculate/*" element={<CalculatorV2 />} />
        <Route path="stock/*" element={<Stock />} />
        <Route path="price/*" element={<PricePages />} />
        <Route path="sales/*" element={<SalesPages />} />
        <Route path="test-reports/*" element={<TestReportsPage />} />
        <Route path="suppliers/*" element={<SuppliersPage />} />
        <Route path="order-scheduler/*" element={<OrderScheduler />} />
        <Route path="supplier-calendar/*" element={<SupplierCalendar />} />
        <Route path="production/*" element={<Production />} />
        <Route path="shipment/*" element={<Shipment />} />
        <Route path="amazon/*" element={<Amazon />} />
        <Route path="amazon/*" element={<Amazon />} />
        <Route path="inc/*" element={<IncPages />} />
        <Route path="odoo/*" element={<OdooPages />} />
        <Route path="materials/*" element={<Materials />} />
        <Route path="assessment/*" element={<Assessment />} />
        <Route path="qc/*" element={<QCPage />} />
        <Route path="sub-supplier/*" element={<SubSupplierPage />} />
        <Route path="analytics/*" element={<Analytics />} />
        <Route path="manage/*" element={<ManageIndex />} />
        <Route path="retailer-store/*" element={<RetailerStorePages />} />
        <Route path="validate/*" element={<Validator />} />
        <Route path="it/*" element={<ITRoutes />} />
        <Route path="accounting/*" element={<AccountingPages />} />
        <Route path="tools/*" element={<ToolsPage />} />
        <Route path="settings/*" element={<UserProfilePage />} />
      </Route>

      <Route path="/barcode" element={<BarcodePage />} />
      <Route path="/sentry-test" element={<SentryTestPage />} />

      <Route path="*" element={<Navigate to="/products" />} />
    </Routes>
  );
};

const QCRoutes = () => {
  return (
    <Routes>
      <Route
        element={
          <ModalProvider>
            <div>
              <Header />
              <div className="relative z-20 text-sm flex flex-1 flex-col min-h-screen transform-gpu duration-300 transition-all md:block md:ml-52">
                <Outlet />
              </div>
            </div>
          </ModalProvider>
        }
      >
        <Route path="qc/*" element={<QCPage />} />
      </Route>

      <Route path="*" element={<Navigate to="/qc" />} />
    </Routes>
  );
};

export default AppRoutes;
